import type { PropsWithChildren, ReactNode } from "react";
import { Badge, Flex, Text, Title } from "rizzui";
import { Breadcrumb, PageTitle } from "@steelyard/theme";

/**
 * A list screen's header: title, the API's own count, breadcrumb, a one-line description, and the
 * screen's actions on the right. COPIED from vendor/isomorphic/apps/isomorphic/src/app/shared/page-header.tsx
 * (the `@container` header, `Title` h2, circle-separated `Breadcrumb`, actions beside it from `@lg`),
 * with the action group's own classes from
 * vendor/isomorphic/apps/isomorphic/src/app/(hydrogen)/ecommerce/products/page.tsx. The template's
 * `PageHeader` takes `title: string` and nothing else and is a copied file this repo does not edit,
 * so the count and the description need a header of their own.
 *
 * `count` is whatever the screen's loader got from the API — a `totalCount`, never a length counted
 * here — and is left off rather than shown as 0 when the screen has none.
 */
export function ListHeader({
  title,
  count,
  description,
  breadcrumb,
  children,
}: PropsWithChildren<{
  title: string;
  count?: number | null;
  description?: ReactNode;
  breadcrumb: { name: string; href?: string }[];
}>) {
  // Title, count and breadcrumb render in the top header (PageTitle), at the Marketplace admin
  // portal's size (text-xl); the description stays here. `children` are for a screen with no
  // `PagedTable` — a list screen hands its buttons to `PagedTable`'s `actions` instead, which puts
  // them at the end of the toolbar row, the Marketplace grid's place for them.
  return (
    <>
      <PageTitle>
        <div className="min-w-0">
          <Flex align="center" gap="3" className="mb-1">
            <Title as="h2" className="truncate text-xl">
              {title}
            </Title>
            {count === undefined || count === null ? null : (
              <Badge variant="flat" rounded="md" className="tabular-nums">
                {count.toLocaleString("en-IN")}
              </Badge>
            )}
          </Flex>
          <Breadcrumb separator="" separatorVariant="circle" className="flex-wrap">
            {breadcrumb.map((item) => (
              <Breadcrumb.Item key={item.name} {...(item.href && { href: item.href })}>
                {item.name}
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>
        </div>
      </PageTitle>
      {description || children ? (
        <header className="@container">
          <div className="flex flex-col gap-3 @lg:flex-row @lg:items-center @lg:justify-between">
            {description ? <Text className="max-w-3xl text-sm text-gray-500">{description}</Text> : <span />}
            {children ? <div className="flex shrink-0 flex-wrap items-center gap-2">{children}</div> : null}
          </div>
        </header>
      ) : null}
    </>
  );
}
